import { supabase } from '@/integrations/supabase/client';

export interface SeedProjectResult {
  project: any;
  batches: any[];
  stagedData: any[];
  signOffs: any[];
  configurations: any[];
}

/**
 * Creates a complete test project with staged data for E2E testing
 */
export async function seedTestProject(
  workspaceId: string,
  options: {
    customerName?: string;
    targetGoLiveDate?: string;
    includeStages?: boolean;
    includeConfigs?: boolean;
  } = {}
): Promise<SeedProjectResult> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const customerName = options.customerName || `E2E Test Company ${Date.now()}`;
  const targetDate = options.targetGoLiveDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  console.log('[testDataSeeder] Creating test project:', customerName);

  // Step 1: Create project
  const { data: project, error: projectError } = await supabase
    .from('tum_onboarding_projects')
    .insert({
      workspace_id: workspaceId,
      project_name: customerName,
      customer_name: customerName,
      customer_contact_name: 'John Test',
      customer_contact_email: 'test@e2ecompany.com',
      target_go_live_date: targetDate,
      estimated_properties: 5,
      estimated_units: 20,
      estimated_accounts: 25,
      current_stage: 'data_import',
      overall_status: 'in_progress',
      completion_percentage: 15,
      created_by: user.id,
      updated_by: user.id,
    })
    .select()
    .single();

  if (projectError) throw projectError;
  console.log('[testDataSeeder] Project created:', project.id);

  // Step 2: Initialize stage sign-offs
  const stages = ['data_import', 'validation', 'mapping', 'configuration', 'review', 'customer_signoff', 'go_live'];
  const signOffs: any[] = [];

  for (const stage of stages) {
    const { data: signOff, error: signOffError } = await supabase
      .from('tum_onboarding_stage_signoffs')
      .insert({
        project_id: project.id,
        stage_name: stage,
        status: 'pending',
        required_signoff: true,
      })
      .select()
      .single();

    if (!signOffError && signOff) signOffs.push(signOff);
  }
  console.log('[testDataSeeder] Sign-offs created:', signOffs.length);

  // Step 3: Create import batch
  const { data: batch, error: batchError } = await supabase
    .from('tum_onboarding_import_batches')
    .insert({
      project_id: project.id,
      batch_type: 'properties', // core_data maps to 'properties' batch type
      filename: 'sample-core-data.csv',
      file_url: 'test-data/sample-core-data.csv',
      file_size_bytes: 1024,
      status: 'completed',
      total_rows: 5,
      rows_processed: 5,
      rows_success: 5,
      rows_failed: 0,
      rows_warning: 0,
      uploaded_by_user_id: user.id,
    })
    .select()
    .single();

  if (batchError) throw batchError;
  console.log('[testDataSeeder] Batch created:', batch.id);

  // Step 4: Create staged data (accounts)
  const testAccounts = [
    { property_name: 'Test Tower A', property_address: '123 Main Street', property_type: 'Multi-Family', account_number: 'ACC-E2E-001', utility_type: 'Electric', supplier_name: 'Duke Energy', bill_source_type: 'Portal', gl_account_code: '6200-1001' },
    { property_name: 'Test Tower A', property_address: '123 Main Street', property_type: 'Multi-Family', account_number: 'ACC-E2E-002', utility_type: 'Water', supplier_name: 'City Water Dept', bill_source_type: 'Email', gl_account_code: '6200-1002' },
    { property_name: 'Test Building B', property_address: '456 Oak Avenue', property_type: 'Commercial', account_number: 'ACC-E2E-003', utility_type: 'Gas', supplier_name: 'Natural Gas Co', bill_source_type: 'Manual', gl_account_code: '6200-1003' },
    { property_name: 'Test Building B', property_address: '456 Oak Avenue', property_type: 'Commercial', account_number: 'ACC-E2E-004', utility_type: 'Electric', supplier_name: 'Georgia Power', bill_source_type: 'API', gl_account_code: '6200-1001' },
    { property_name: 'Test Plaza C', property_address: '789 Pine Road', property_type: 'Retail', account_number: 'ACC-E2E-005', utility_type: 'Electric', supplier_name: 'Southern Company', bill_source_type: 'Portal', gl_account_code: '6200-1001' },
  ];

  const stagedData: any[] = [];
  for (let i = 0; i < testAccounts.length; i++) {
    const account = testAccounts[i];
    const { data: staged, error: stagedError } = await (supabase as any)
      .from('tum_onboarding_staged_data')
      .insert({
        project_id: project.id,
        batch_id: batch.id,
        row_number: i + 1,
        source_data: account,
        mapped_data: account,
        validation_status: 'valid',
        validation_issues: [],
        import_status: 'pending',
      })
      .select()
      .single();

    if (!stagedError && staged) stagedData.push(staged);
  }
  console.log('[testDataSeeder] Staged data created:', stagedData.length);

  // Step 5: Create account configurations if requested
  const configurations: any[] = [];
  if (options.includeConfigs) {
    for (const staged of stagedData) {
      const { data: config, error: configError } = await (supabase as any)
        .from('onboarding_account_configurations')
        .insert({
          project_id: project.id,
          imported_account_id: staged.id,
          bill_collection_method: staged.mapped_data.bill_source_type?.toLowerCase() || 'manual',
          vendor_id: null,
          gl_account_code: staged.mapped_data.gl_account_code,
          collection_completed: true,
          parsing_completed: true,
          validation_completed: true,
          gl_mapping_completed: true,
        })
        .select()
        .single();

      if (!configError && config) configurations.push(config);
    }
    console.log('[testDataSeeder] Configurations created:', configurations.length);
  }

  // Step 6: Approve stages if requested (for testing later stages)
  if (options.includeStages) {
    const stagesToApprove = ['data_import', 'validation', 'mapping', 'configuration', 'review'];
    for (const stage of stagesToApprove) {
      await supabase
        .from('tum_onboarding_stage_signoffs')
        .update({
          status: 'approved',
          signed_by_user_id: user.id,
          signed_at: new Date().toISOString(),
          comments: 'Auto-approved for E2E testing',
        })
        .eq('project_id', project.id)
        .eq('stage_name', stage);
    }
    console.log('[testDataSeeder] Stages approved for testing');

    // Update project stage
    await supabase
      .from('tum_onboarding_projects')
      .update({
        current_stage: 'go_live',
        completion_percentage: 90,
      })
      .eq('id', project.id);
  }

  return {
    project,
    batches: [batch],
    stagedData,
    signOffs,
    configurations,
  };
}

/**
 * Cleans up test data by customer name pattern
 */
export async function cleanupTestProjects(workspaceId: string, customerNamePattern: string = 'E2E Test'): Promise<number> {
  const { data: projects, error } = await supabase
    .from('tum_onboarding_projects')
    .select('id, customer_name')
    .eq('workspace_id', workspaceId)
    .ilike('customer_name', `%${customerNamePattern}%`);

  if (error) throw error;
  if (!projects?.length) return 0;

  const projectIds = projects.map(p => p.id);
  console.log(`[testDataSeeder] Cleaning up ${projectIds.length} test projects`);

  // Delete in order (respecting foreign keys)
  for (const projectId of projectIds) {
    // Delete configurations
    await (supabase as any).from('onboarding_account_configurations').delete().eq('project_id', projectId);
    // Delete staged data
    await (supabase as any).from('tum_onboarding_staged_data').delete().eq('project_id', projectId);
    // Delete batches
    await supabase.from('tum_onboarding_import_batches').delete().eq('project_id', projectId);
    // Delete sign-offs
    await supabase.from('tum_onboarding_stage_signoffs').delete().eq('project_id', projectId);
    // Delete project
    await supabase.from('tum_onboarding_projects').delete().eq('id', projectId);
  }

  return projectIds.length;
}

/**
 * Quick test to verify database connectivity and table access
 */
export async function verifyDatabaseAccess(): Promise<{ success: boolean; tables: string[]; errors: string[] }> {
  const tables = [
    'tum_onboarding_projects',
    'tum_onboarding_import_batches',
    'tum_onboarding_staged_data',
    'tum_onboarding_stage_signoffs',
    'tum_onboarding_customer_invitations',
    'tum_onboarding_import_changes',
    'tum_onboarding_customer_contributions',
    'onboarding_account_configurations',
    'tum_properties',
    'tum_units',
    'tum_utility_accounts',
  ];

  const errors: string[] = [];
  const accessibleTables: string[] = [];

  for (const table of tables) {
    try {
      const { error } = await (supabase as any).from(table).select('id').limit(1);
      if (error) {
        errors.push(`${table}: ${error.message}`);
      } else {
        accessibleTables.push(table);
      }
    } catch (e: any) {
      errors.push(`${table}: ${e.message}`);
    }
  }

  return {
    success: errors.length === 0,
    tables: accessibleTables,
    errors,
  };
}

/**
 * Generate sample customer invitation for testing sign-off workflow
 */
export async function createTestInvitation(
  projectId: string,
  workspaceId: string,
  email: string = 'customer@test.com'
): Promise<any> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const token = `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const { data: invitation, error } = await (supabase as any)
    .from('tum_onboarding_customer_invitations')
    .insert({
      project_id: projectId,
      workspace_id: workspaceId,
      token,
      customer_email: email,
      customer_name: 'Test Customer',
      status: 'pending',
      created_by: user.id,
    })
    .select()
    .single();

  if (error) throw error;
  return invitation;
}
